import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getKernel } from '../../utils/api';
import FlowConnector from '../ProcessingView/FlowConnector';
import MarketCards from '../ProcessingView/MarketCards';
import './KernelProcess.css';
import '../ProcessingView/ProcessingJourney.css';

/**
 * KernelProcessPetrified - Static view of a completed processing journey
 * Route: /kernelprocessp/:kernelId
 * Shows all stages instantly without streaming or auto-scroll
 */
const KernelProcessPetrified = () => {
  const { kernelId } = useParams();
  const navigate = useNavigate();
  const [kernel, setKernel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch kernel once on mount
  useEffect(() => {
    const fetchKernel = async () => {
      try {
        const response = await getKernel(kernelId);
        setKernel(response.kernel);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    fetchKernel();
  }, [kernelId]);

  if (loading) {
    return (
      <div className="kernel-process">
        <div className="kernel-process__loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kernel-process">
        <div className="kernel-process__error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="kernel-process__back-btn">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Extract data
  const intakeText = kernel?.intake_agent_thoughts;
  const publicText = kernel?.public_agent_thoughts;
  const searchQuestion = kernel?.search_question;
  const isRejected = kernel?.public_agent_decision === 'No';
  const markets = kernel?.affected_markets;
  const selectedMarket = kernel?.selected_market;
  const aggregationQuestions = kernel?.aggregation_questions;
  const projection = kernel?.projection;
  const isComplete = kernel?.status === 'projection_complete';

  // All stages show if they have data
  const showStage2 = !!intakeText;
  const showStage3 = !!publicText && !isRejected;
  const showStage4 = markets?.length > 0;
  const showStage5 = aggregationQuestions?.length > 0;
  const showStage6 = isComplete && projection;

  return (
    <div className="kernel-process">
      <header className="kernel-process__header">
        <div className="kernel-process__header-content">
          <img src="/logo.png" alt="Arbituer" className="kernel-process__logo" />
          <div className="kernel-process__header-text">
            <h1 className="kernel-process__title">Arbituer</h1>
            <p className="kernel-process__subtitle">Market Intelligence</p>
          </div>
        </div>
      </header>

      <main className="kernel-process__main">
        <div className="processing-journey processing-journey--petrified">
          <div className="processing-journey__container">
            {/* Header */}
            <div className="processing-journey__header">
              <span className="processing-journey__kernel-id">Kernel: {kernelId}</span>
            </div>

            {/* Stage 1: Intake Assessment */}
            {intakeText && (
              <div className="journey-stage journey-stage--visible">
                <div className="journey-stage__box">
                  <h3 className="journey-stage__title">
                    Assessing if the user is an expert and their belief is worth investigating further.
                  </h3>
                  <div className="journey-stage__content">
                    <span>{intakeText}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Connector 1→2 */}
            {showStage2 && <FlowConnector animated={false} />}

            {/* Stage 2: Public Information Check */}
            {showStage2 && (
              <div className="journey-stage journey-stage--visible">
                <div className="journey-stage__box">
                  <h3 className="journey-stage__title">
                    Cross-checking novelty of belief with public information
                  </h3>
                  {searchQuestion && (
                    <div className="journey-stage__subtitle">
                      <span className="journey-stage__label">Research Question:</span>
                      <p className="journey-stage__question">{searchQuestion}</p>
                    </div>
                  )}
                  {publicText && (
                    <div className="journey-stage__content">
                      <span>{publicText}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Rejection State */}
            {publicText && isRejected && (
              <>
                <FlowConnector animated={false} />
                <div className="journey-stage journey-stage--visible">
                  <div className="journey-stage__box journey-stage__box--rejected">
                    <div className="journey-stage__rejected-icon">X</div>
                    <h3 className="journey-stage__title journey-stage__title--rejected">
                      Information Not Accepted
                    </h3>
                    <p className="journey-stage__rejected-text">
                      This belief appears to be redundant with publicly available information.
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Connector 2→3 */}
            {showStage3 && <FlowConnector animated={false} />}

            {/* Stage 3: Market Discovery - 1.2x wider */}
            {showStage3 && (
              <div className="journey-stage journey-stage--visible journey-stage--wide-sm">
                <div className="journey-stage__box">
                  <h3 className="journey-stage__title">
                    This belief is worth investigating. Finding related markets.
                  </h3>
                  {showStage4 && (
                    <MarketCards
                      markets={markets}
                      selectedId={selectedMarket?.conditionId || selectedMarket?.id}
                      onAnimationComplete={() => {}}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Connector 3→4 */}
            {showStage4 && <FlowConnector animated={false} />}

            {/* Stage 4: Aggregation Questions */}
            {showStage4 && (
              <>
                {/* Title box - normal width */}
                <div className="journey-stage journey-stage--visible">
                  <div className="journey-stage__box">
                    <h3 className="journey-stage__title journey-stage__title--no-margin">
                      Analyzing this underlying market and building a set of public information regarding it
                    </h3>
                  </div>
                </div>

                <FlowConnector animated={false} height="24px" />

                {/* Questions grid - 1.5x wider */}
                <div className="journey-stage journey-stage--visible journey-stage--wide-lg">
                  {showStage5 && (
                    <div className="journey-stage__aggregation-grid">
                      {aggregationQuestions.slice(0, 3).map((qa, index) => (
                        <div key={index} className="aggregation-box">
                          <p className="aggregation-box__question">{qa.question}</p>
                          <div className="aggregation-box__answer">
                            <span>{qa.answer}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Connector 4→5 */}
            {showStage5 && <FlowConnector animated={false} />}

            {/* Stage 5: Dual Projections - 1.2x wider */}
            {showStage5 && (
              <div className="journey-stage journey-stage--visible journey-stage--wide-sm">
                <div className="journey-stage__projections-row">
                  {/* Public-Only Projection */}
                  <div className="projection-box projection-box--public">
                    <h4 className="projection-box__title">Public Forecast</h4>
                    <p className="projection-box__subtitle">Analysis using only public information</p>
                    {projection?.publicPricing && (
                      <>
                        <div className="projection-box__value">
                          {((Number(projection.publicPricing.fairValueLow) +
                            Number(projection.publicPricing.fairValueHigh)) / 2 * 100
                          ).toFixed(1)}%
                        </div>
                        <div className="projection-box__range">
                          Range: {(projection.publicPricing.fairValueLow * 100).toFixed(0)}% -{' '}
                          {(projection.publicPricing.fairValueHigh * 100).toFixed(0)}%
                        </div>
                        <div className="projection-box__reasoning">
                          <span>{projection.publicPricing.reasoning}</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Private-Info Projection */}
                  <div className="projection-box projection-box--private">
                    <h4 className="projection-box__title">With Your Information</h4>
                    <p className="projection-box__subtitle">Analysis including your private belief</p>
                    {projection?.privatePricing && (
                      <>
                        <div className="projection-box__value">
                          {((Number(projection.privatePricing.fairValueLow) +
                            Number(projection.privatePricing.fairValueHigh)) / 2 * 100
                          ).toFixed(1)}%
                        </div>
                        <div className="projection-box__range">
                          Range: {(projection.privatePricing.fairValueLow * 100).toFixed(0)}% -{' '}
                          {(projection.privatePricing.fairValueHigh * 100).toFixed(0)}%
                        </div>
                        <div className="projection-box__reasoning">
                          <span>{projection.privatePricing.reasoning}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Stage 6: Complete - View Results Button */}
            {showStage6 && (
              <>
                <FlowConnector animated={false} />
                <div className="journey-stage journey-stage--visible">
                  <div className="journey-stage__complete">
                    <h3 className="journey-stage__complete-title">Analysis Complete</h3>
                    <p className="journey-stage__complete-text">
                      Your information has been processed and valued against the market.
                    </p>
                    <button
                      className="journey-stage__view-btn"
                      onClick={() => navigate(`/kernels/${kernelId}`)}
                    >
                      View Results
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default KernelProcessPetrified;
